import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, Panel, Button, IconButton, Tag, Banner, EmptyState, Spinner, Dialog, ConfirmDialog,
  Field, TextInput, TextArea, Select, Checkbox, DataTable, SimpleTable, StatGrid, StatTile, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { apiError } from '../../next/ui';
import {
  FRAME_OPTIONS, FRAME_LABELS, AUDIENCE_OPTIONS, AUDIENCE_LABELS, SOURCE_LABELS, ORDER_STATUS,
  emptyCampaignForm, campaignToForm, campaignStateLabel, formatDay, downloadBlob,
} from './gemaraOrdersShared';

/**
 * הזמנות גמרא — new design. כרכים פתוחים להזמנה (הנבחנים מזמינים בסיום המבחן, בשלוחה 1/4 · 2/4 ובאתר),
 * ההזמנות לפי עיר, ייצוא רשימת מסירה לשליחי הערים וסימון "נמסר".
 */
export default function GemaraOrdersNext() {
  const toast = useCkToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [orders, setOrders] = useState([]);
  const [byCity, setByCity] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [dialog, setDialog] = useState(null); // { id|null, form }
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/gemara-orders/campaigns');
      const list = data.data || [];
      setCampaigns(list);
      setSelectedId((cur) => (cur && list.some((c) => c._id === cur) ? cur : list[0]?._id || ''));
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת הכרכים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadOrders = useCallback(async () => {
    if (!selectedId) { setOrders([]); setByCity([]); return; }
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const [o, c] = await Promise.all([
        api.get('/gemara-orders', { params: { campaignId: selectedId } }),
        api.get('/gemara-orders/by-city', { params: { campaignId: selectedId } }),
      ]);
      setOrders(o.data.data || []);
      setByCity(c.data.data || []);
    } catch (e) {
      setOrdersError(apiError(e, 'שגיאה בטעינת ההזמנות'));
    } finally {
      setOrdersLoading(false);
    }
  }, [selectedId]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const selected = campaigns.find((c) => c._id === selectedId) || null;
  const refreshAll = () => { loadCampaigns(); loadOrders(); };

  const saveCampaign = async () => {
    setSaving(true);
    try {
      const body = { ...dialog.form };
      if (dialog.id) {
        await api.put(`/gemara-orders/campaigns/${dialog.id}`, body);
        toast.success('הכרך עודכן');
      } else {
        const { data } = await api.post('/gemara-orders/campaigns', body);
        setSelectedId(data._id);
        toast.success('הכרך נוצר — פתחו אותו להזמנה כשמוכן');
      }
      setDialog(null);
      loadCampaigns();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
    } finally {
      setSaving(false);
    }
  };

  const setCampaignStatus = async (c, status) => {
    try {
      await api.put(`/gemara-orders/campaigns/${c._id}`, { status });
      toast.success(status === 'open' ? 'ההזמנה נפתחה — מוצעת עכשיו בקו ובאתר' : 'ההזמנה נסגרה');
      loadCampaigns();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    }
  };

  const removeCampaign = async () => {
    try {
      await api.delete(`/gemara-orders/campaigns/${deleteTarget._id}`);
      toast.success('הכרך נמחק');
      loadCampaigns();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקה'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const setOrderStatus = async (o, status) => {
    try {
      await api.patch(`/gemara-orders/${o._id}`, { status });
      loadOrders();
      loadCampaigns();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון ההזמנה'));
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get('/gemara-orders/export-excel', { params: { campaignId: selectedId }, responseType: 'blob' });
      downloadBlob(res.data, `הזמנות_גמרא_${selected?.title || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      toast.error('שגיאה בייצוא');
    } finally {
      setExporting(false);
    }
  };

  const visibleOrders = useMemo(
    () => (showCancelled ? orders : orders.filter((o) => o.status !== 'cancelled')),
    [orders, showCancelled]
  );
  const totals = useMemo(() => ({
    total: byCity.reduce((s, r) => s + r.total, 0),
    delivered: byCity.reduce((s, r) => s + r.delivered, 0),
    cities: byCity.length,
  }), [byCity]);

  const setForm = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const toggleFrame = (frame, on) => setForm({
    frames: on ? [...new Set([...(dialog.form.frames || []), frame])] : (dialog.form.frames || []).filter((f) => f !== frame),
  });

  const columns = [
    { key: 'memberName', label: 'שם', render: (o) => <b>{o.memberName || '—'}</b> },
    { key: 'city', label: 'עיר', type: 'enum', getValue: (o) => o.city || 'ללא עיר' },
    { key: 'phone', label: 'טלפון', ltr: true, getValue: (o) => o.phone || '—' },
    { key: 'source', label: 'הוזמן דרך', type: 'enum', getValue: (o) => SOURCE_LABELS[o.source] || o.source },
    { key: 'frame', label: 'מסגרת', type: 'enum', getValue: (o) => FRAME_LABELS[o.frame] || '—' },
    { key: 'createdAt', label: 'תאריך', getValue: (o) => o.createdAt, render: (o) => formatHebrewDateTime(o.createdAt) },
    {
      key: 'status',
      label: 'סטטוס',
      type: 'enum',
      getValue: (o) => ORDER_STATUS[o.status]?.label || o.status,
      render: (o) => <Tag tone={ORDER_STATUS[o.status]?.tone}>{ORDER_STATUS[o.status]?.label || o.status}</Tag>,
    },
  ];

  const state = selected ? campaignStateLabel(selected) : null;

  return (
    <div className="nx-stack">
      <PageHeader
        title="הזמנות גמרא"
        sub="כרך פתוח מוצע לנבחנים בתחילת המבחן ובסיומו ובאתר; כרך פתוח לכולם גם בשלוחה 1/4 · 2/4. כל חבר מזמין פעם אחת לכל כרך."
        primary={{ label: 'כרך חדש', onClick: () => setDialog({ id: null, form: emptyCampaignForm() }) }}
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: refreshAll }]}
      />
      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={loadCampaigns}>נסו שוב</Button>}>{error}</Banner> : null}

      {loading ? <div className="ck-empty"><Spinner size={26} /></div> : !campaigns.length ? (
        <EmptyState
          icon="book"
          title="אין כרכים להזמנה"
          action={<Button variant="primary" icon="plus" onClick={() => setDialog({ id: null, form: emptyCampaignForm() })}>כרך חדש</Button>}
        >
          צרו כרך (למשל "מסכת חולין כרך ב"), בחרו מסגרות ופתחו אותו להזמנה.
        </EmptyState>
      ) : (
        <>
          {campaigns.length > 1 ? (
            <div style={{ maxWidth: 360 }}>
              <Field label="כרך">
                <Select
                  value={selectedId}
                  onChange={setSelectedId}
                  options={campaigns.map((c) => ({ value: c._id, label: `${c.title} (${c.orderCount})` }))}
                />
              </Field>
            </div>
          ) : null}

          {selected ? (
            <Panel
              title={selected.title}
              sub={[
                (selected.frames || []).map((f) => FRAME_LABELS[f]).join(' · '),
                AUDIENCE_LABELS[selected.audience || 'examinees'],
                selected.opensAt ? `נפתח ${formatDay(selected.opensAt)}` : '',
                selected.closesAt ? `אחרון להזמנה ${formatDay(selected.closesAt)}` : 'ללא תאריך סגירה',
              ].filter(Boolean).join(' · ')}
              icon="book"
              tone={selected.isOpenNow ? 'primary' : 'neutral'}
              actions={(
                <>
                  <Tag tone={state.tone}>{state.label}</Tag>
                  {selected.status === 'open'
                    ? <Button size="sm" icon="pause" onClick={() => setCampaignStatus(selected, 'closed')}>סגירת ההזמנה</Button>
                    : <Button size="sm" variant="primary" icon="check" onClick={() => setCampaignStatus(selected, 'open')}>פתיחה להזמנה</Button>}
                  <IconButton icon="edit" label="עריכה" onClick={() => setDialog({ id: selected._id, form: campaignToForm(selected) })} />
                  <IconButton
                    icon="trash"
                    label={selected.orderCount ? 'יש הזמנות — אפשר רק לסגור' : 'מחיקה'}
                    disabled={!!selected.orderCount}
                    onClick={() => setDeleteTarget(selected)}
                  />
                </>
              )}
            >
              {selected.notes ? <p className="nx-desc">{selected.notes}</p> : null}
            </Panel>
          ) : null}

          <StatGrid>
            <StatTile label="הזמנות" value={totals.total.toLocaleString('he-IL')} icon="book" tone="primary" loading={ordersLoading && !orders.length} />
            <StatTile label="נמסרו" value={totals.delivered.toLocaleString('he-IL')} icon="check" loading={ordersLoading && !orders.length} />
            <StatTile label="ערים" value={totals.cities} icon="flag" loading={ordersLoading && !orders.length} />
          </StatGrid>

          {byCity.length ? (
            <Panel
              title="לפי עיר"
              sub="רשימת המסירה לשליח כל עיר — בייצוא, גיליון לכל עיר"
              icon="flag"
              actions={<Button size="sm" icon="download" disabled={exporting} onClick={exportExcel}>{exporting ? 'מייצא…' : 'ייצוא Excel לשליחים'}</Button>}
            >
              <SimpleTable
                rowKey="city"
                rows={byCity}
                columns={[
                  { key: 'city', label: 'עיר', render: (r) => <b>{r.city}</b> },
                  { key: 'total', label: 'הזמנות', render: (r) => r.total },
                  { key: 'delivered', label: 'נמסרו', render: (r) => r.delivered },
                ]}
              />
            </Panel>
          ) : null}

          <Checkbox checked={showCancelled} onChange={setShowCancelled} label="הצגת הזמנות מבוטלות" />
          <DataTable
            mode="client"
            rows={visibleOrders}
            rowKey="_id"
            columns={columns}
            loading={ordersLoading}
            error={ordersError}
            onRetry={loadOrders}
            onRefresh={loadOrders}
            storageKey="bchinot-gemara-orders"
            selectable={false}
            exportFilename="הזמנות_גמרא"
            emptyTitle="אין עדיין הזמנות לכרך הזה"
            rowActions={(o) => [
              ...(o.status !== 'delivered' ? [{ key: 'delivered', icon: 'check', label: 'סימון נמסר', onClick: () => setOrderStatus(o, 'delivered') }] : []),
              ...(o.status !== 'ordered' ? [{ key: 'ordered', icon: 'undo', label: 'החזרה ל"הוזמן"', onClick: () => setOrderStatus(o, 'ordered') }] : []),
              ...(o.status !== 'cancelled' ? [{ key: 'cancel', icon: 'x', label: 'ביטול הזמנה', onClick: () => setOrderStatus(o, 'cancelled') }] : []),
            ]}
          />
        </>
      )}

      <Dialog
        open={!!dialog}
        onClose={() => !saving && setDialog(null)}
        title={dialog?.id ? 'עריכת כרך' : 'כרך חדש להזמנה'}
        icon="book"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={saving} onClick={() => setDialog(null)}>ביטול</Button>
            <Button
              variant="primary"
              disabled={saving || !dialog?.form.title.trim() || !dialog?.form.frames?.length}
              onClick={saveCampaign}
            >
              {saving ? 'שומר…' : dialog?.id ? 'שמירה' : 'יצירה'}
            </Button>
          </>
        )}
      >
        {dialog ? (
          <div className="nx-stack">
            <Field label="שם הכרך *"><TextInput value={dialog.form.title} onChange={(e) => setForm({ title: e.target.value })} placeholder="מסכת חולין כרך ב" data-autofocus /></Field>
            <Field label="מוצע לנבחני">
              <div className="nx-row" style={{ gap: 16, flexWrap: 'wrap' }}>
                {FRAME_OPTIONS.map((f) => (
                  <Checkbox key={f.value} checked={dialog.form.frames.includes(f.value)} onChange={(on) => toggleFrame(f.value, on)} label={f.label} />
                ))}
              </div>
            </Field>
            <Field label="מי יכול להזמין">
              <Select
                value={dialog.form.audience}
                onChange={(audience) => setForm({ audience })}
                options={AUDIENCE_OPTIONS.map((a) => ({ value: a.value, label: a.label }))}
              />
            </Field>
            <p className="nx-desc">{AUDIENCE_OPTIONS.find((a) => a.value === dialog.form.audience)?.hint}</p>
            <div className="nx-row" style={{ gap: 12 }}>
              <div style={{ flex: 1 }}><Field label="פתיחה (רשות)"><TextInput type="date" value={dialog.form.opensAt} onChange={(e) => setForm({ opensAt: e.target.value })} /></Field></div>
              <div style={{ flex: 1 }}><Field label="יום אחרון להזמנה (רשות)"><TextInput type="date" value={dialog.form.closesAt} onChange={(e) => setForm({ closesAt: e.target.value })} /></Field></div>
            </div>
            <p className="nx-desc">ביום שלפני היום האחרון מושמעת בקו ההודעה "ההזמנה נסגרת מחר".</p>
            <Field label="הערות"><TextArea value={dialog.form.notes} onChange={(e) => setForm({ notes: e.target.value })} rows={2} /></Field>
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={removeCampaign}
        title="מחיקת כרך"
        message={<p className="ck-confirm__msg">{`למחוק את הכרך «${deleteTarget?.title || ''}»?`}</p>}
        confirmLabel="מחיקה"
      />
    </div>
  );
}
